import { Navbar } from '../home/components/Navbar';
import { Footer } from '../home/components/Footer';

export default function ComplianceReviewPage() {
  const scrollToContact = () => {
    window.REACT_APP_NAVIGATE('/property-assessment');
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-[#F0F0F0] to-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="text-xs font-semibold text-[#882629] uppercase tracking-widest mb-6">
            Service Details
          </div>
          <h1 className="font-['Fraunces'] text-5xl lg:text-6xl font-bold text-[#505A5A] mb-8">
            Compliance and Eligibility Review
          </h1>
          <p className="text-xl text-[#505A5A]/80 leading-relaxed max-w-3xl">
            Get crystal-clear answers about what you can and cannot do with your property. We review city rules, zoning regulations, and strata bylaws so you know exactly where you stand—no guesswork, just facts.
          </p>
        </div>
      </section>

      {/* What's Included */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <h2 className="font-['Fraunces'] text-4xl font-bold text-[#505A5A] mb-12">
            What's included in your review
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                icon: 'ri-government-line',
                title: 'City bylaw analysis',
                description: 'We examine your municipal short-term rental regulations, licensing requirements, and any recent policy changes that affect your property.'
              },
              {
                icon: 'ri-map-pin-line',
                title: 'Zoning verification',
                description: 'We confirm your property\'s zoning designation and whether short-term or medium-term rentals are permitted in your zone.'
              },
              {
                icon: 'ri-building-line',
                title: 'Strata bylaw review',
                description: 'For condos and townhouses, we review your strata bylaws and rental restriction rules to determine if rentals are allowed.'
              },
              {
                icon: 'ri-file-list-3-line',
                title: 'Compliance checklist',
                description: 'You receive a detailed report outlining what is allowed, what is prohibited, and what steps you need to take to stay compliant.'
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Risk assessment',
                description: 'We identify potential compliance risks and provide recommendations to avoid fines, penalties, or legal issues.'
              },
              {
                icon: 'ri-question-answer-line',
                title: 'Expert consultation',
                description: 'A 30-minute call to walk through your report, answer questions, and discuss your best path forward.'
              }
            ].map((item, index) => (
              <div key={index} className="bg-[#F0F0F0] rounded-2xl p-8">
                <div className="w-12 h-12 flex items-center justify-center mb-6">
                  <i className={`${item.icon} text-[#882629] text-4xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-[#505A5A] mb-4">
                  {item.title}
                </h3>
                <p className="text-base text-[#505A5A]/70 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why This Matters */}
      <section className="py-20 bg-[#F0F0F0]">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <h2 className="font-['Fraunces'] text-4xl font-bold text-[#505A5A] mb-8">
            Why this matters
          </h2>
          <div className="space-y-6">
            <div className="bg-white rounded-2xl p-8">
              <h3 className="text-xl font-semibold text-[#505A5A] mb-4">
                Avoid costly mistakes
              </h3>
              <p className="text-base text-[#505A5A]/70 leading-relaxed">
                Operating a short-term rental without proper compliance can result in fines ranging from $1,000 to $10,000+ per violation. Some cities actively enforce these rules and issue penalties to non-compliant hosts.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8">
              <h3 className="text-xl font-semibold text-[#505A5A] mb-4">
                Save time and stress
              </h3>
              <p className="text-base text-[#505A5A]/70 leading-relaxed">
                Navigating city bylaws, zoning maps, and strata documents can be overwhelming and time-consuming. We do the research for you and deliver clear, actionable answers.
              </p>
            </div>
            <div className="bg-white rounded-2xl p-8">
              <h3 className="text-xl font-semibold text-[#505A5A] mb-4">
                Make informed decisions
              </h3>
              <p className="text-base text-[#505A5A]/70 leading-relaxed">
                Whether you're considering short-term rentals, medium-term furnished rentals, or long-term leasing, our review helps you understand all your options and choose the best strategy for your property.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <h2 className="font-['Fraunces'] text-4xl font-bold text-[#505A5A] mb-12">
            Who this service is for
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-home-heart-line',
                title: 'Homeowners',
                description: 'Considering renting out your primary residence or a secondary property'
              },
              {
                icon: 'ri-building-4-line',
                title: 'Condo owners',
                description: 'Need to verify strata bylaws and rental restrictions before listing'
              },
              {
                icon: 'ri-user-search-line',
                title: 'Property buyers',
                description: 'Evaluating a property purchase and want to know rental potential before buying'
              }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 flex items-center justify-center mx-auto mb-6">
                  <i className={`${item.icon} text-[#882629] text-5xl`}></i>
                </div>
                <h3 className="text-xl font-semibold text-[#505A5A] mb-4">
                  {item.title}
                </h3>
                <p className="text-base text-[#505A5A]/70 leading-relaxed">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-20 bg-[#F0F0F0]">
        <div className="max-w-3xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-['Fraunces'] text-4xl font-bold text-[#505A5A] mb-6">
            Simple, transparent pricing
          </h2>
          <div className="bg-white rounded-3xl p-12 shadow-lg">
            <div className="text-5xl font-bold text-[#882629] mb-4">
              $199
            </div>
            <p className="text-lg text-[#505A5A]/70 mb-8">
              One-time fee • Comprehensive report • 30-minute consultation included
            </p>
            <button 
              onClick={scrollToContact}
              className="bg-[#882629] text-white px-10 py-4 rounded-full text-base font-medium hover:bg-[#6E1F22] transition-all cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl"
            >
              Get your compliance review
            </button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <h2 className="font-['Fraunces'] text-4xl font-bold text-[#505A5A] mb-12 text-center">
            Frequently asked questions
          </h2>
          <div className="space-y-6">
            {[
              {
                question: 'How long does the review take?',
                answer: 'Most reviews are completed within 3-5 business days. Rush service is available for an additional fee if you need results sooner.'
              },
              {
                question: 'What cities do you cover?',
                answer: 'We cover Vancouver, North Vancouver, Burnaby, Surrey, Langley, Victoria, Whistler, Calgary, Edmonton, Toronto, and surrounding areas. Contact us if your city is not listed.'
              },
              {
                question: 'What if my property is not eligible?',
                answer: 'We will explain why and explore alternative options such as medium-term furnished rentals, long-term leasing, or other strategies to maximize your property\'s income potential.'
              },
              {
                question: 'Do you provide legal advice?',
                answer: 'No, we are not lawyers. We provide research and guidance based on publicly available bylaws and regulations. For legal advice, we recommend consulting with a real estate lawyer.'
              },
              {
                question: 'Is the consultation fee refundable?',
                answer: 'The $199 fee is non-refundable, but if you proceed with our co-hosting or management services, we will credit the full amount toward your first month of service.'
              }
            ].map((item, index) => (
              <div key={index} className="bg-[#F0F0F0] rounded-2xl p-8">
                <h3 className="text-lg font-semibold text-[#505A5A] mb-3">
                  {item.question}
                </h3>
                <p className="text-base text-[#505A5A]/70 leading-relaxed">
                  {item.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-[#882629]">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <h2 className="font-['Fraunces'] text-4xl lg:text-5xl font-bold text-white mb-6">
            Ready to get clarity on your property?
          </h2>
          <p className="text-xl text-white/90 mb-10 leading-relaxed">
            Stop guessing. Get a professional compliance review and know exactly what you can do with your property.
          </p>
          <button 
            onClick={scrollToContact}
            className="bg-white text-[#882629] px-10 py-4 rounded-full text-base font-medium hover:bg-[#F0F0F0] transition-all cursor-pointer whitespace-nowrap shadow-lg hover:shadow-xl"
          >
            Start your review today
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
